import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '../services/auth'

export const useAuthStore = defineStore('auth', () => {
  // État
  const isAuthenticated = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const token = ref<string | null>(null)

  // Getters
  const hasValidToken = computed(() => {
    return isAuthenticated.value && token.value && authService.isTokenValid()
  })

  // Actions
  async function initialize() {
    isLoading.value = true
    error.value = null

    try {
      // Vérifier si un token existe déjà
      if (authService.isTokenValid()) {
        token.value = await authService.getAccessToken()
        isAuthenticated.value = true
        console.log('✅ Token existant valide récupéré')
      } else {
        // Authentifier avec client credentials
        await authenticate()
      }
    } catch (err) {
      console.error('❌ Erreur lors de l\'initialisation de l\'authentification:', err)
      error.value = 'Erreur d\'authentification'
      isAuthenticated.value = false
    } finally {
      isLoading.value = false
    }
  }

  async function authenticate() {
    isLoading.value = true
    error.value = null

    try {
      console.log('🔐 Authentification en cours...')
      token.value = await authService.authenticateClientCredentials()
      isAuthenticated.value = true
      console.log('✅ Authentification réussie')
    } catch (err) {
      console.error('❌ Erreur lors de l\'authentification:', err)
      error.value = 'Échec de l\'authentification'
      isAuthenticated.value = false
      token.value = null
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function refreshToken() {
    if (isLoading.value) return

    isLoading.value = true
    error.value = null

    try {
      console.log('🔄 Rafraîchissement du token...')
      token.value = await authService.refreshTokenIfNeeded()
      isAuthenticated.value = true
      console.log('✅ Token rafraîchi avec succès')
    } catch (err) {
      console.error('❌ Erreur lors du rafraîchissement:', err)
      error.value = 'Erreur de rafraîchissement du token'
      await logout()
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    isLoading.value = true

    try {
      authService.logout()
      isAuthenticated.value = false
      token.value = null
      error.value = null
      console.log('✅ Déconnexion réussie')
    } catch (err) {
      console.error('❌ Erreur lors de la déconnexion:', err)
    } finally {
      isLoading.value = false
    }
  }

  // Intercepteur pour gérer les erreurs 401
  function handleAuthError() {
    isAuthenticated.value = false
    token.value = null
    error.value = 'Session expirée'
  }

  return {
    // État
    isAuthenticated,
    isLoading,
    error,
    token,
    // Getters
    hasValidToken,
    // Actions
    initialize,
    authenticate,
    refreshToken,
    logout,
    handleAuthError
  }
})
